import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';
import { nodeTypeSurface } from '@/lib/node-visuals';
import { DRAFT_TYPE_CONFIG } from './draft-item-config';
import { Link as LinkIcon, Check, X, Pencil, ArrowLeft, Loader2, AlertCircle, Plus } from 'lucide-react';
import type { DraftItem } from '@/types/dialog';

interface JobOption {
  id: string;
  title: string;
}

interface DialogReviewProps {
  items: DraftItem[];
  onUpdate: (id: string, updates: Partial<DraftItem>) => void;
  onReject: (id: string) => void;
  onConfirm: (id: string) => void;
  onBackToChat: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  submitError?: string | null;
}

export function DialogReview({ items, onUpdate, onReject, onConfirm, onBackToChat, onSubmit, submitting, submitError }: DialogReviewProps) {
  const visibleItems = items.filter((i) => i.status !== 'rejected');
  const confirmedCount = visibleItems.filter((i) => i.status === 'confirmed').length;
  const canSubmit = confirmedCount > 0;

  const grouped = {
    job: visibleItems.filter((i) => i.type === 'job'),
    challenge: visibleItems.filter((i) => i.type === 'challenge'),
    opportunity: visibleItems.filter((i) => i.type === 'opportunity'),
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-background to-muted py-4 px-4 sm:py-8">
      <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-2rem)] sm:h-[calc(100vh-4rem)] bg-card rounded-xl shadow-lg border overflow-hidden">
        {/* Header */}
        <div className="px-4 py-4 border-b bg-card/80 backdrop-blur-sm">
          <h1 className="text-lg font-semibold">Review what we captured</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Edit anything, then tick what you want to share. Unticked items stay private.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
          {visibleItems.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nothing captured yet. Go back to the chat to share more.
            </p>
          )}
          {(['job', 'challenge', 'opportunity'] as const).map((type) => {
            const group = grouped[type];
            if (group.length === 0) return null;
            const config = DRAFT_TYPE_CONFIG[type];
            return (
              <div key={type} className="space-y-2">
                <SectionLabel>
                  {config.label}s ({group.length})
                  <span className="ml-1.5 normal-case tracking-normal font-normal text-muted-foreground">
                    {config.gloss}
                  </span>
                </SectionLabel>
                {group.map((item) => (
                  <ReviewCard
                    key={item.id}
                    item={item}
                    availableJobs={grouped.job
                      .filter((j) => j.id !== item.id)
                      .map((j) => ({ id: j.id, title: j.title }))}
                    onUpdate={onUpdate}
                    onReject={onReject}
                    onConfirm={onConfirm}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t bg-card">
          {submitError && (
            <div
              role="alert"
              aria-live="polite"
              className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
          <div className="px-4 py-4 flex items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={onBackToChat} disabled={submitting}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to chat
            </Button>
            <p className="text-xs text-muted-foreground">
              {confirmedCount} of {visibleItems.length} selected to submit
            </p>
            <Button size="sm" onClick={onSubmit} disabled={!canSubmit || submitting}>
              {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ReviewCardProps {
  item: DraftItem;
  availableJobs: JobOption[];
  onUpdate: (id: string, updates: Partial<DraftItem>) => void;
  onReject: (id: string) => void;
  onConfirm: (id: string) => void;
}

function ReviewCard({ item, availableJobs, onUpdate, onReject, onConfirm }: ReviewCardProps) {
  const config = DRAFT_TYPE_CONFIG[item.type];
  const Icon = config.icon;
  const isConfirmed = item.status === 'confirmed';

  return (
    <div
      className={cn('rounded-lg border p-3 transition-colors', !isConfirmed && 'opacity-80')}
      style={{ borderColor: nodeTypeSurface(config.hue).borderColor }}
    >
      <div className="flex items-start gap-2">
        <Badge tone={item.type} className="shrink-0">
          <Icon className="h-3 w-3" />
          {config.label}
        </Badge>
        <div className="flex-1 min-w-0 space-y-1.5">
          <EditableField
            value={item.title}
            placeholder="Title"
            className="font-medium text-sm"
            multiline={false}
            required
            maxLength={2000}
            onSave={(value) =>
              onUpdate(item.id, {
                title: value,
                status: 'confirmed',
                edited: true,
                editedAt: new Date().toISOString(),
              })
            }
          />
          <EditableField
            value={item.description ?? ''}
            placeholder="Add a description (optional)"
            className="text-xs text-muted-foreground"
            multiline
            maxLength={5000}
            onSave={(value) =>
              onUpdate(item.id, {
                description: value || undefined,
                status: 'confirmed',
                edited: true,
                editedAt: new Date().toISOString(),
              })
            }
          />
          {item.type !== 'job' && (
            <ParentLinks
              parentJobIds={item.parentJobIds}
              availableJobs={availableJobs}
              onChange={(ids) =>
                onUpdate(item.id, {
                  parentJobIds: ids,
                  status: 'confirmed',
                  edited: true,
                  editedAt: new Date().toISOString(),
                })
              }
            />
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button
            size="icon"
            variant={isConfirmed ? 'default' : 'outline'}
            className="h-7 w-7"
            onClick={() => onConfirm(item.id)}
            aria-label={isConfirmed ? 'Unconfirm' : 'Confirm'}
            aria-pressed={isConfirmed}
            title={isConfirmed ? 'Unconfirm' : 'Confirm'}
          >
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => onReject(item.id)}
            aria-label="Remove"
            title="Remove"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface EditableFieldProps {
  value: string;
  placeholder: string;
  className?: string;
  multiline: boolean;
  required?: boolean;
  maxLength: number;
  onSave: (value: string) => void;
}

function EditableField({ value, placeholder, className, multiline, required, maxLength, onSave }: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(value);
    setEditing(true);
  };

  const commit = () => {
    const trimmed = draft.trim();
    // Reject empty commits on required fields — keep the old value, exit edit mode.
    if (required && !trimmed) {
      setDraft(value);
      setEditing(false);
      return;
    }
    if (trimmed !== value.trim()) {
      onSave(trimmed);
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  if (editing) {
    return multiline ? (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={2}
        className={cn(
          'w-full px-2 py-1 border rounded bg-background focus:outline-hidden focus:ring-1 focus:ring-primary resize-none',
          className,
        )}
      />
    ) : (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        className={cn(
          'w-full px-2 py-1 border rounded bg-background focus:outline-hidden focus:ring-1 focus:ring-primary',
          className,
        )}
      />
    );
  }

  const isEmpty = !value.trim();
  return (
    <button
      onClick={startEdit}
      className={cn(
        'w-full text-left px-2 py-1 rounded hover:bg-muted/50 cursor-text flex items-center gap-1 group',
        className,
        isEmpty && 'text-muted-foreground italic',
      )}
    >
      <span className="flex-1 min-w-0 wrap-break-word">{isEmpty ? placeholder : value}</span>
      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-50 shrink-0" />
    </button>
  );
}

interface ParentLinksProps {
  parentJobIds: string[];
  availableJobs: JobOption[];
  onChange: (ids: string[]) => void;
}

/** Inline editor for the set of jobs a challenge or opportunity links to.
 *  Renders each linked job as a removable chip and shows unlinked candidates
 *  inline so the respondent can connect to multiple jobs without leaving the
 *  card. JTBD theory routinely involves a single challenge impeding several
 *  jobs (or an opportunity lifting several), and the prompt encourages
 *  multi-link where genuinely applicable. */
function ParentLinks({ parentJobIds, availableJobs, onChange }: ParentLinksProps) {
  // Sanitize against known job ids — a stale id (e.g. for a job the respondent
  // rejected upstream) would otherwise sit invisibly in state with no UI to
  // remove it. Render and edit operate on the sanitized set; if there's stale
  // state, the next save naturally drops it.
  const validJobIds = new Set(availableJobs.map((j) => j.id));
  const sanitized = parentJobIds.filter((id) => validJobIds.has(id));
  const linkedSet = new Set(sanitized);
  const linked = sanitized
    .map((id) => availableJobs.find((j) => j.id === id))
    .filter((j): j is JobOption => Boolean(j));
  const unlinked = availableJobs.filter((j) => !linkedSet.has(j.id));

  const truncate = (t: string) => (t.length > 50 ? t.slice(0, 50) + '…' : t);

  const remove = (id: string) => onChange(sanitized.filter((i) => i !== id));
  // Dedup on add to guard against fast double-clicks adding the same id twice.
  const add = (id: string) => onChange(sanitized.includes(id) ? sanitized : [...sanitized, id]);

  if (availableJobs.length === 0 && linked.length === 0) {
    // Nothing to link to; show a quiet hint instead of an empty control.
    return (
      <p className="flex items-center gap-1 text-xs text-muted-foreground opacity-50 italic">
        <LinkIcon className="h-2.5 w-2.5 shrink-0" />
        No jobs to link to yet
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      <LinkIcon className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground">linked to:</span>
      {linked.length === 0 && (
        <span className="text-muted-foreground italic opacity-70">none yet</span>
      )}
      {linked.map((job) => (
        <span
          key={job.id}
          className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground"
        >
          <span>{truncate(job.title)}</span>
          <button
            type="button"
            onClick={() => remove(job.id)}
            className="text-muted-foreground hover:text-destructive transition-colors"
            aria-label={`Unlink from ${job.title}`}
            title="Unlink"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
      {unlinked.map((job) => (
        <button
          key={job.id}
          type="button"
          onClick={() => add(job.id)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 px-2 py-0.5 text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
          aria-label={`Link to ${job.title}`}
          title={`Link to ${job.title}`}
        >
          <Plus className="h-2.5 w-2.5" />
          <span>{truncate(job.title)}</span>
        </button>
      ))}
    </div>
  );
}
